import React from 'react';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';


import { lightPalette } from '@app/theme';
import { ScrollableTable } from '@components/ui/ScrollableTable';

import { tableHeaderCellSx, zebraRowSx } from '../analysisReport.utilities';
import { ScoreChip } from '../ScoreChip';

import type { KeyMetricsTableProperties } from './KeyMetricsTable.types';

const MONO_FONT = "'JetBrains Mono', monospace";

export const KeyMetricsTable: React.FC<KeyMetricsTableProperties> = ({
  metrics,
  dataTest = 'key-metrics-table',
}) => {
  return (
    <ScrollableTable dataTest={dataTest} ariaLabel='Ключевые метрики'>
      <Table size='small' aria-label='Ключевые метрики' sx={{ fontSize: 14 }}>
        <TableHead>
          <TableRow>
            {['Метрика', 'Значение', 'Норма', 'Оценка'].map((header) => (
              <TableCell
                key={header}
                data-testid={`${dataTest}_header_${header.toLowerCase()}`}
                sx={tableHeaderCellSx}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {metrics.map((metric, index) => {
            return (
              <TableRow
                key={`${metric.name}-${index}`}
                data-testid={`${dataTest}_row_${index}`}
                sx={zebraRowSx}
              >
                <TableCell
                  data-testid={`${dataTest}_row_${index}_name`}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: lightPalette.text.primary,
                    letterSpacing: '-0.01em',
                    borderBottom: `1px solid ${lightPalette.border.light}`,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box>
                    <Typography
                      component='span'
                      sx={{ fontSize: 14, fontWeight: 500, color: lightPalette.text.primary }}
                    >
                      {metric.name}
                    </Typography>
                    {metric.note && (
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: lightPalette.text.muted,
                          mt: 0.25,
                          lineHeight: 1.4,
                        }}
                      >
                        {metric.note}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                <TableCell
                  data-testid={`${dataTest}_row_${index}_value`}
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: 13,
                    fontWeight: 500,
                    color: lightPalette.text.primary,
                    borderBottom: `1px solid ${lightPalette.border.light}`,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  {metric.value}
                </TableCell>

                <TableCell
                  data-testid={`${dataTest}_row_${index}_benchmark`}
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: 13,
                    color: lightPalette.text.secondary,
                    borderBottom: `1px solid ${lightPalette.border.light}`,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  {metric.benchmark}
                </TableCell>

                <TableCell
                  data-testid={`${dataTest}_row_${index}_score`}
                  sx={{
                    borderBottom: `1px solid ${lightPalette.border.light}`,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <ScoreChip
                    score={metric.score}
                    maxScore={10}
                    height={24}
                    fontSize={12}
                    labelPx={1}
                    showBorder={false}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ScrollableTable>
  );
};
